import React from 'react'
import {withToggles} from 'startlibs/lib/hocs'
import {Dialog, Button} from 'startlibs/lib/components'
import {withForm, Form, AutoComplete, Field, Errors, SimpleCheckbox} from 'startlibs/lib/form'
import {ice} from 'icepath'
import styled, {css} from 'styled-components'
import {Toggle, ToggleContainer} from '../../components/Toggle'

@withToggles('confirmEmailDialog')
@withForm()
export class SendEmailDialog extends React.PureComponent {
  componentDidMount() {
    if (!this.props.attachedReport && this.props.isPHIPossible) {
      this.props.form.connector.setValue('sharedReportViewPHIAuthenticationRequired', !this.props.sharingOptions || this.props.sharingOptions.sendSecurely !== false)
    }
  }

  handleSubmit = () => {
    const emails = this.getAllEmails()
    return this.props.sendEmailFetcher({emails,sharedReportViewPHIAuthenticationRequired:this.props.form.properties.sharedReportViewPHIAuthenticationRequired}).catch((e) => {
      this.props.form.connector.setErrors({emails: ['E-mail could not be sent. Please check your internet connection and try again.']})
      return {errors: true}
    })
  }

  onSuccess = () => {
    this.props.confirmSent()
    this.props.closeDialog()
  }

  static validateEmail(email) {
    const re = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
    return re.test(email)
  }

  getAllEmails = () => {
    const userEmail = this.props.userEmail
    return this.getEmailList()
      .filter(e => e)
      .concat(this.props.form.properties.sendToSelf && SendEmailDialog.validateEmail(userEmail) ? [userEmail] : [])
  }

  validateAndConfirm = (e) => {
    e && e.preventDefault()
    const typedEmails = this.getAllEmails()
    const invalidEmails = typedEmails.filter(e => !SendEmailDialog.validateEmail(e))
    if (invalidEmails.length) {
      this.props.form.connector.clearErrors()
    } else if (!typedEmails.length) {
      this.props.form.connector.setErrors({
        emails: [`At least one valid e-mail address is needed.`]
      })
    } else {
      this.props.confirmEmailDialog.open()
      this.props.form.connector.clearErrors()
    }
  }

  addEmail = (email) => {
    const form = this.props.form
    const emails = form.properties.emails || []
    const lastIsEditing = emails.slice(-1).filter(_ => typeof _ === 'string').length
    form.connector.setValue('emails', ice.spliceIn(emails, [], lastIsEditing ? -1 : emails.length, 0, email))
  }

  getEmailList = () => (this.props.form.properties.emails || []).map(_ => _.email || _)

  emailValidation = (v) => SendEmailDialog.validateEmail(v.trim()) ? {success: {email: v.trim()}} : {errors: [`"${v}" is not a valid e-mail address.`]}

  getEmailSuggestions = (s) => {
    const iLastEmail = s.toLowerCase()
    const hasArroba = s.indexOf('@') >= 0
    return this.props.reportRecipients.registeredUsers.filter(({email, first_name, last_name}) =>
      (hasArroba ?
          (email.toLowerCase().indexOf(iLastEmail) === 0 || (first_name + ' ' + last_name).toLowerCase().indexOf(iLastEmail) === 0) :
          (email.toLowerCase().indexOf(iLastEmail) >= 0 || (first_name + ' ' + last_name).toLowerCase().indexOf(iLastEmail) >= 0)
      )
    )
  }

  getEmailLabel = (_) => _.first_name ? (_.first_name + (_.last_name ? ' ' + _.last_name : '')) : _.email

  render() {
    const {closeDialog, attachedReport, userEmail, reportInfoCard, confirmEmailDialog, reportRecipients, form, shareDialogMessage, isPHIPossible} = this.props
    const canSendToSelf = SendEmailDialog.validateEmail(userEmail)
    const emails = (form.properties.emails || []).map(_ => _.email)
    const suggested = reportRecipients.suggestedUsers.filter(_ => emails.indexOf(_) < 0).map(email => reportRecipients.registeredUsers.find(_ => _.email === email) || {email})
    return !confirmEmailDialog.isOpen ?
      (
        <Dialog
          closeDialog={closeDialog}
          confirm={this.validateAndConfirm}
          title="Send report via e-mail"
          className='dialog-send-email'
        >
          <form onSubmit={this.validateAndConfirm}>
            <Dialog.Content>
              {reportInfoCard}
              <p className="main-instruction">{shareDialogMessage}</p>
              {
                !attachedReport &&
                <ToggleContainer disabled={!isPHIPossible} onClick={() => form.connector.setValue('sharedReportViewPHIAuthenticationRequired', !form.properties.sharedReportViewPHIAuthenticationRequired)}>
                  <div>Send securely (recommended)<div className="help-text">Requires the recipient to know the patient’s date of birth and last name.</div></div><Toggle checked={form.properties.sharedReportViewPHIAuthenticationRequired}/>
                </ToggleContainer>
              }
              <AutoComplete
                form={form}
                getLabel={this.getEmailLabel}
                minQueryLength={1}
                confirmIfNewKeys={[' ']}
                defaultValue={[]}
                autoSelect
                validation={this.emailValidation}
                fetchSuggestions={this.getEmailSuggestions}
                path="emails"
                label="Email address"
              />

              {suggested.length > 0 &&
              <div className="suggested-emails"><span>Suggestions: </span>
                {suggested.map((email, i) =>
                  <span key={email.email}>
                          <a
                            onClick={() => this.addEmail(email)}>{email.first_name || email.last_name ? email.first_name + ' ' + email.last_name : email.email}</a>
                    {i + 1 === suggested.length ? '' : ', '}
                        </span>
                  )}
                </div>
                }
              {
                canSendToSelf &&
                <div className="checkbox-list">
                  <SimpleCheckbox
                    form={form}
                    path="sendToSelf"
                    label={`Send a copy to my email (${userEmail})`}
                  />
                </div>
              }
              <Errors form={form} />
            </Dialog.Content>
            <Dialog.Footer>
              <button onClick={closeDialog} type="button">Cancel</button>
              <button type="submit" className="highlight with-loader">Send</button>
            </Dialog.Footer>
          </form>
        </Dialog>
      ) : (
        <Dialog
          title="Confirm sending report"
          closeDialog={closeDialog}
          confirm={this.handleSubmit}
          className='dialog-send-email'
        >
          <Dialog.Content>
            <div className="box">
              <span className="bold">Sending report to: </span>
              <ul className="sending-email-list">
                {this.getAllEmails().map(email => reportRecipients.registeredUsers.find(_ => _.email === email) || {email})
                  .map(({email, first_name, last_name}) =>
                    <li
                      key={email}>{first_name || last_name ? first_name + ' ' + last_name + ' - ' + email : email}</li>
                  )}
              </ul>
              {
                !attachedReport &&
                (
                  form.properties.sharedReportViewPHIAuthenticationRequired
                ? <div className="securely-sending-status"><b>Securely sending enabled.</b> Recipient(s) will need to
                  enter the patient’s date of birth and last name to access the report.</div>
                :  <div className="securely-sending-status alert"><b>Securely sending disabled.</b> Anyone with the
                  report link will be able to access it.</div>
                )
              }
            </div>
            <div className="additional-info">
              <h4>This email may contain confidential and protected health care information.</h4>
              <p>Please be sure that the email of the recipient has been entered correctly
                and that you are using appropriately confidential mechanisms for this transmission.</p>
            </div>
          </Dialog.Content>
          <Dialog.Footer>
            <Form form={form} onSuccess={this.onSuccess} handleSubmit={this.handleSubmit}>
              <Button onClick={confirmEmailDialog.close}>Return</Button>
              <Button autoFocus highlight isLoading={form.isLoading} type="submit">Confirm
                send</Button>
            </Form>
          </Dialog.Footer>
        </Dialog>
      )

  }
}



// WEBPACK FOOTER //
// ./~/report-components/src/modules/viewer/SendEmailDialog.jsx